<template>
  <header class="osce-header" :class="headerClasses">
    <!-- Left Section: Branding and Exam Info -->
    <div class="header-left">
      <div class="branding">
        <div @click="goToDashboard">
          <span class="logo-text">OSCE Manager</span>
        </div>
        <div class="exam-info">
          <span class="exam-name">{{ examTitle }}</span>
          <span class="exam-date" v-if="examDate">{{ formattedDate }}</span>
        </div>
      </div>
    </div>

    <!-- Center Section: Real-time Status -->
    <div class="header-center">
      <!-- <ConnectionStatus 
        :status="connectionStatus"
        :last-sync="lastSync"
        @reconnect="handleReconnect"
      />
       -->
     

      <div v-if="currentSession" class="session-info">
        <span class="session-time">
          ⏱️ Session: {{ formatSessionTime }}
        </span>
        <span class="candidate-progress" v-if="showProgress">
          📊 {{ completedCandidates }}/{{ totalCandidates }} candidates
        </span>
      </div>
    </div>

    <!-- Right Section: User and Controls -->
    <div class="header-right">
      <!-- Quick Actions -->
      <div class="quick-actions">
        <button 
          v-if="showEmergencyStop"
          @click="handleEmergencyStop"
          class="action-btn emergency-stop"
          title="Emergency Stop - Pause Exam"
        >
          🚨 Stop
        </button>
        
        <button 
          @click="toggleFullscreen"
          class="action-btn fullscreen-btn"
          :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
        >
          {{ isFullscreen ? '📱' : '🖥️' }}
        </button>

        <button 
          @click="showShortcutsHelp"
          class="action-btn help-btn"
          title="Keyboard Shortcuts"
        >
          ⌨️
        </button>
      </div>

      <!-- Notifications -->
      <NotificationBell 
        :notifications="notifications"
        @notification-click="handleNotificationClick"
        @clear-all="clearNotifications"
      />

      <!-- Mobile Menu Toggle -->
      <button 
        v-if="isMobile"
        @click="toggleMobileMenu"
        class="mobile-menu-toggle"
        :class="{ active: mobileMenuOpen }"
      >
       
      </button>
    </div>

    <!-- Mobile Menu Overlay -->
    <!-- <MobileMenuOverlay 
      v-if="isMobile && mobileMenuOpen"
      :user="currentUser"
      :notifications="notifications"
      @close="mobileMenuOpen = false"
      @action="handleMobileMenuAction"
    /> -->
  </header>
</template>

<script>//
//import ConnectionStatus from 'src/components/OSCE/contraptions/ConnectionStatus.vue'
import NotificationBell from 'src/components/OSCE/contraptions/NotificationBell.vue'
//import MobileMenuOverlay from 'src/components/OSCE/contraptions/MobileMenuOverlay.vue'

export default {
  name: 'OSCEInfo',
  components: {
    //ConnectionStatus,
    NotificationBell,
    //MobileMenuOverlay
  },
  props: {
    examTitle: {
      type: String,
      default: 'OSCE Examination'
    },
    examDate: {
      type: String,
      default: null
    },
    examinerName: {
      type: String,
      required: true
    },
    connectionStatus: {
      type: String,
      default: 'connected', // 'connected', 'disconnected', 'reconnecting'
      validator: (value) => ['connected', 'disconnected', 'reconnecting', 'error'].includes(value)
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    lastSaved: {
      type: String,
      default: null
    },
    currentSession: {
      type: Object,
      default: null
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    completedCandidates: {
      type: Number,
      default: 0
    },
    totalCandidates: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isFullscreen: false,
      mobileMenuOpen: false,
      hasSaveErrors: false,
      lastSync: new Date().toISOString(),
      notifications: [],
      currentUser: {
        name: this.examinerName,
        avatar: null,
        id: 'examiner_001'
      },
      userRole: 'examiner',
      sessionStartTime: null,
      showEmergencyStop: true
    }
  },
  
  computed: {
    headerClasses() {
      return {
        'saving': this.isSaving,
        'disconnected': this.connectionStatus === 'disconnected',
        'mobile': this.isMobile,
        'fullscreen': this.isFullscreen
      }
    },
    
    formattedDate() {
      if (!this.examDate) return ''
      return new Date(this.examDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    
    formatSessionTime() {
      if (!this.sessionStartTime) return '0:00'
      
      const now = new Date()
      const start = new Date(this.sessionStartTime)
      const diff = Math.floor((now - start) / 1000) // difference in seconds
      
      const hours = Math.floor(diff / 3600)
      const minutes = Math.floor((diff % 3600) / 60)
      
      return `${hours}:${minutes.toString().padStart(2, '0')}`
    },
    
    isMobile() {
      return window.innerWidth < 768
    }
  },
  
  watch: {
    connectionStatus(newStatus) {
      if (newStatus === 'disconnected') {
        this.addNotification({
          id: Date.now(),
          type: 'warning',
          title: 'Connection Lost',
          message: 'Attempting to reconnect...',
          timestamp: new Date().toISOString(),
          persistent: true
        })
      } else if (newStatus === 'connected') {
        this.removeNotification('connection-lost')
        this.addNotification({
          id: Date.now(),
          type: 'success',
          title: 'Connection Restored',
          message: 'All changes have been synchronized',
          timestamp: new Date().toISOString(),
          autoClear: true
        })
        this.lastSync = new Date().toISOString()
      }
    },
    
    isSaving(newValue) {
      if (!newValue && this.lastSaved) {
        // Just finished saving
        this.hasSaveErrors = false
      }
    }
  },
  
  mounted() {
    this.initializeHeader()
    this.setupEventListeners()
    this.loadUserData()
    this.startSessionTimer()
  },
  
  beforeDestroy() {
    this.cleanupEventListeners()
    clearInterval(this.sessionTimer)
  },
  
  methods: {
    initializeHeader() {
      this.sessionStartTime = new Date().toISOString()
      this.setupFullscreenListener()
    },
    
    setupEventListeners() {
      // Online/offline detection
      window.addEventListener('online', this.handleOnline)
      window.addEventListener('offline', this.handleOffline)
      
      // Keyboard shortcuts
      document.addEventListener('keydown', this.handleKeyboardShortcuts)
      
      // Resize handler for mobile detection
      window.addEventListener('resize', this.handleResize)
    },
    
    cleanupEventListeners() {
      window.removeEventListener('online', this.handleOnline)
      window.removeEventListener('offline', this.handleOffline)
      document.removeEventListener('keydown', this.handleKeyboardShortcuts)
      window.removeEventListener('resize', this.handleResize)
    },
    
    setupFullscreenListener() {
      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)
    },
    
    // User and session management
    loadUserData() {
      // Load user data from Vuex or API
      const userData = this.$store?.state.user || {}
      this.currentUser = { ...this.currentUser, ...userData }
      
      // Load notifications
      this.loadNotifications()
    },
    
    async loadNotifications() {
      try {
        const notifications = await this.$api.getNotifications()
        this.notifications = notifications.slice(0, 5) // Show only 5 most recent
      } catch (error) {
        console.error('Failed to load notifications:', error)
      }
    },
    
    startSessionTimer() {
      this.sessionTimer = setInterval(() => {
        // Force recomputation of session time
        this.$forceUpdate()
      }, 60000) // Update every minute
    },
    
    // Connection handling
    handleOnline() {
      this.$emit('connection-change', 'connected')
    },
    
    handleOffline() {
      this.$emit('connection-change', 'disconnected')
    },
    
    async handleReconnect() {
      this.$emit('reconnect-attempt')
      try {
        await this.$api.reconnect()
        this.$emit('connection-change', 'connected')
      } catch (error) {
        console.error('Reconnection failed:', error)
        this.$emit('connection-change', 'error')
      }
    },
    
    // Fullscreen functionality
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen()
      } else {
        this.exitFullscreen()
      }
    },
    
    enterFullscreen() {
      const element = document.documentElement
      
      if (element.requestFullscreen) {
        element.requestFullscreen()
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen()
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen()
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen()
      }
    },
    
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    },
    
    handleFullscreenChange() {
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )
    },
    
    // Notifications
    addNotification(notification) {
      this.notifications.unshift(notification)
      
      if (notification.autoClear) {
        setTimeout(() => {
          this.removeNotification(notification.id)
        }, 5000)
      }
      
      // Limit notifications
      if (this.notifications.length > 10) {
        this.notifications = this.notifications.slice(0, 10)
      }
    },
    
    removeNotification(notificationId) {
      this.notifications = this.notifications.filter(n => n.id !== notificationId)
    },
    
    clearNotifications() {
      this.notifications = []
    },
    
    handleNotificationClick(notification) {
      this.$emit('notification-clicked', notification)
      
      if (!notification.persistent) {
        this.removeNotification(notification.id)
      }
    },
    
    // Emergency functionality
    handleEmergencyStop() {
      if (confirm('Are you sure you want to emergency stop the exam? This will pause all stations.')) {
        this.$emit('emergency-stop')
        
        this.addNotification({
          id: Date.now(),
          type: 'emergency',
          title: 'Exam Paused',
          message: 'All stations have been paused by emergency stop',
          timestamp: new Date().toISOString(),
          persistent: true
        })
      }
    },
    
    // Navigation
    goToDashboard() {
      this.$router.push('/dashboard')
    },
    
    // User actions
    handleLogout() {
      if (confirm('Are you sure you want to logout?')) {
        this.$emit('logout')
      }
    },
    
    showProfile() {
      this.$emit('profile-click')
    },
    
    showSettings() {
      this.$emit('settings-click')
    },
    
    showShortcutsHelp() {
      this.$emit('shortcuts-help')
    },
    
    // Mobile menu
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    
    handleMobileMenuAction(action) {
      this.mobileMenuOpen = false
      
      switch (action) {
        case 'profile':
          this.showProfile()
          break
        case 'settings':
          this.showSettings()
          break
        case 'logout':
          this.handleLogout()
          break
        case 'help':
          this.showShortcutsHelp()
          break
      }
    },
    
    // Keyboard shortcuts
    handleKeyboardShortcuts(event) {
      // Ctrl/Cmd + / for shortcuts help
      if ((event.ctrlKey || event.metaKey) && event.key === '/') {
        event.preventDefault()
        this.showShortcutsHelp()
      }
      
      // Ctrl/Cmd + F for fullscreen
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault()
        this.toggleFullscreen()
      }
      
      // Escape to close mobile menu
      if (event.key === 'Escape' && this.mobileMenuOpen) {
        this.mobileMenuOpen = false
      }
    },
    
    handleResize() {
      // Close mobile menu when resizing to desktop
      if (!this.isMobile && this.mobileMenuOpen) {
        this.mobileMenuOpen = false
      }
    },
    
    // Public methods that can be called from parent
    showSuccessMessage(message) {
      this.addNotification({
        id: Date.now(),
        type: 'success',
        title: 'Success',
        message: message,
        timestamp: new Date().toISOString(),
        autoClear: true
      })
    },
    
    showErrorMessage(message) {
      this.addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Error',
        message: message,
        timestamp: new Date().toISOString(),
        persistent: true
      })
    },
    
    updateSaveStatus(isSaving, hasErrors = false) {
      this.isSaving = isSaving
      this.hasSaveErrors = hasErrors
      
      if (!isSaving && !hasErrors) {
        this.lastSaved = new Date().toISOString()
      }
    }
  }
}
</script>

<style scoped>
.osce-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  height: 64px;
  margin: 20px;
  /* color: white;
  //box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); */
  position: relative;
  z-index: 100;
  transition: all 0.3s ease;
}

.osce-header.saving {
  background: linear-gradient(135deg, #ffd89b 0%, #19547b 100%);
}

.osce-header.disconnected {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
}

.osce-header.mobile {
  padding: 0 15px;
  height: 56px;
}

/* Left Section */
.header-left {
  display: flex;
  align-items: center;
  flex: 1;
}

.branding {
  display: flex;
  align-items: center;
  gap: 20px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.logo:hover {
  background: rgba(255, 255, 255, 0.1);
}

.logo-icon {
  font-size: 1.5em;
}

.logo-text {
  font-weight: 700;
  font-size: 1.2em;
  letter-spacing: -0.5px;
}

.exam-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.exam-name {
  font-weight: 600;
  font-size: 1em;
}

.exam-date {
  font-size: 0.8em;
  opacity: 0.9;
}

/* Center Section */
.header-center {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  justify-content: center;
}

.session-info {
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.9em;
}

.session-time, .candidate-progress {
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
}

/* Right Section */
.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
  justify-content: flex-end;
}

.quick-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.emergency-stop {
  background: rgba(220, 53, 69, 0.8);
  border-color: rgba(220, 53, 69, 0.6);
}

.emergency-stop:hover {
  background: rgba(220, 53, 69, 1);
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  gap: 3px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
}

.mobile-menu-toggle span {
  width: 20px;
  height: 2px;
  background: white;
  transition: all 0.3s ease;
}

.mobile-menu-toggle.active span:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.mobile-menu-toggle.active span:nth-child(2) {
  opacity: 0;
}

.mobile-menu-toggle.active span:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -6px);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-center {
    gap: 10px;
  }
  
  .session-info {
    flex-direction: column;
    gap: 5px;
  }
}

@media (max-width: 768px) {
  .osce-header {
    padding: 0 15px;
    height: 56px;
  }
  
  .logo-text {
    display: none;
  }
  
  .exam-info {
    display: none;
  }
  
  .header-center {
    display: none;
  }
  
  .quick-actions .action-btn span {
    display: none;
  }
  
  .mobile-menu-toggle {
    display: flex;
  }
}

@media (max-width: 480px) {
  .quick-actions {
    gap: 4px;
  }
  
  .action-btn {
    padding: 6px;
  }
  
  .action-btn span {
    display: none;
  }
}

/* Animation for saving state */
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}

.osce-header.saving .header-center {
  animation: pulse 2s infinite;
}
</style>